/* Margin Finder: onde há margem para economizar, por sessão e por usuário.
 *
 * Esta casca não sabe nada sobre telecom. Ela conhece SESSÕES — cada uma dona da
 * própria consulta, do próprio <select> de usuário e do próprio JSON, porque cada
 * agente responde com um formato diferente e um pai que buscasse por todos precisaria
 * de um tipo-união e recarregaria telecom enquanto se olha outra coisa.
 *
 * E, desde 09/09/2026, ela não conhece nem a LISTA de sessões: as chips saem de
 * `/api/margin/agentes`, que lê os `agent_id` distintos da tabela. Um agente novo
 * aparece sem deploy, desenhado por `<Generico>`; um que já tenha tela própria entra
 * por `CURADAS` e a genérica some para ele. Somar uma seção curada é somar uma linha
 * ali — e tirar uma linha de `margin.CURADOS`, no servidor. */

import { useMemo } from "react";
import type { ComponentType } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { MargemAgentesPayload } from "../lib/api";
import { agentLabel } from "../lib/agentes";
import { ErrorState, PageHead, Skeleton } from "../components/ui";
import { Telecom } from "./margin/Telecom";
import { Seguros } from "./margin/Seguros";
import { Saude } from "./margin/Saude";
import { Assinaturas } from "./margin/Assinaturas";
import { Perfil } from "./margin/Perfil";
import { Generico } from "./margin/Generico";

/* `Curada` OU `agentId`, nunca os dois. O registro guarda DADO e não um componente
 * montado na hora: `Render: () => <Generico …/>` criaria um tipo de componente novo a
 * cada render deste pai, e o React desmontaria a seção inteira — perdendo o usuário
 * escolhido e refazendo a consulta — a cada mexida na querystring. */
interface Section { key: string; label: string; Curada?: ComponentType; agentId?: string }

/** Quem já tem tela própria. O servidor tem a lista dele (`margin.CURADOS`, para saber
 *  que cache esquentar); esta aqui é a única que sabe quais COMPONENTES existem. */
const CURADAS: Record<string, Section> = {
  agent_profile: { key: "perfil", label: "Perfil", Curada: Perfil },
  agent_telecom: { key: "telecom", label: "Telecom", Curada: Telecom },
  agent_seguros: { key: "seguros", label: "Seguros", Curada: Seguros },
  agent_saude: { key: "saude", label: "Saúde", Curada: Saude },
  agent_assinaturas: { key: "assinaturas", label: "Assinaturas", Curada: Assinaturas },
};

/** A ordem de leitura da aba. Perfil primeiro: é o retrato de quem é a pessoa, e as
 *  outras seções são o que fazer com isso. Agente fora desta lista entra depois, por
 *  rótulo — inclusive um que apareça amanhã. */
const ORDEM = ["agent_profile", "agent_telecom", "agent_seguros", "agent_saude",
               "agent_assinaturas"];

/** Sem descoberta, a aba não morre: mostra o que existia antes dela. */
const RESERVA: Section[] = [CURADAS.agent_profile, CURADAS.agent_telecom,
                            CURADAS.agent_seguros, CURADAS.agent_saude,
                            CURADAS.agent_assinaturas];

const posicao = (id: string) => {
  const i = ORDEM.indexOf(id);
  return i < 0 ? ORDEM.length : i;
};

export function MarginFinder() {
  const [params, setParams] = useSearchParams();

  const q = useQuery({
    // Sob o prefixo ["margin"], que `AgentsRefresh` já invalida — então "Atualizar
    // dados" também descobre agentes novos, sem uma linha a mais lá.
    queryKey: ["margin", "agentes"],
    queryFn: () => api<MargemAgentesPayload>("/api/margin/agentes"),
    staleTime: Infinity,
    gcTime: 12 * 60 * 60_000,
    retry: false,
    placeholderData: (previous) => previous,
  });

  const sections = useMemo<Section[]>(() => {
    const achados = q.data?.agentes ?? [];
    if (achados.length === 0) return RESERVA;
    return [...achados]
      .sort((x, y) => posicao(x.agent_id) - posicao(y.agent_id)
        || agentLabel(x.agent_id).localeCompare(agentLabel(y.agent_id), "pt-BR"))
      .map((a) => CURADAS[a.agent_id]
        ?? { key: a.agent_id, label: agentLabel(a.agent_id), agentId: a.agent_id });
  }, [q.data]);

  const key = params.get("sessao") || sections[0].key;
  const section = sections.find((s) => s.key === key) ?? sections[0];

  const pick = (value: string) => {
    const next = new URLSearchParams(params);
    if (value && value !== sections[0].key) next.set("sessao", value);
    else next.delete("sessao");
    /* O `?usuario=` ATRAVESSA a troca de sessão, e é o que dá sentido às chips: ler o
     * perfil de alguém e então ver, do mesmo alguém, o que telecom e seguros acharam.
     * Limpar aqui obrigava a reencontrar o usuário em cada aba.
     *
     * Antes isto apagava, com o receio de "o outro agente pode não ter esse usuário".
     * Medido na base: os 35 usuários com perfil aparecem inteiros em telecom, seguros,
     * saúde e custo financeiro; só assinaturas tem 5 de fora. O caso existe, e quem o
     * trata é `SecaoMargin` -- que diz qual usuário está faltando em vez de sumir com
     * a seleção. */
    setParams(next, { replace: true });
  };

  return (
    <>
      <PageHead eyebrow="margin finder" title="Margin Finder">
        Onde os agentes acharam margem para economizar, um usuário por vez. A economia
        estimada vem do agente; os números abaixo dela são o raciocínio que o levou lá.
      </PageHead>

      {q.isPending ? <Skeleton rows={10} /> : (
        <>
          {q.error && (
            <ErrorState error={q.error} onRetry={() => q.refetch()} />
          )}

          <div className="controls">
            <div className="chips" role="group" aria-label="Sessão">
              {sections.map((s) => (
                <button key={s.key} className="chip" aria-pressed={s.key === section.key}
                        onClick={() => pick(s.key)}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {section.agentId
            ? <Generico agentId={section.agentId} />
            : section.Curada && <section.Curada />}
        </>
      )}
    </>
  );
}
